// Shared low-level helpers for DDL structure extractors. Pure functions, no
// tree-sitter — same posture as the SR-Edit DML extractor's `dml.shared.ts`.
//
// PRD: Ralph/DdlStructuralEdit/PRD.md § Slice: DDL Structure Extractor — CREATE TABLE.

import type { ParseOptions } from '../../../sql-grammar/model';
import { skipContextFor, trySkipNonCode } from '../../classify/tokenSkip';
import { readIdentifier, isBareIdentStart } from '../../scope/identifiers';

/** Skip whitespace + line/block comments forward starting at `i`. Returns the
 *  first non-skippable index in `[i, end)`, or `end` if everything is
 *  skippable. **Does not** consume quoted identifiers (`[ ]`, `` ` ` ``, `" "`)
 *  — those are handled by `readIdentifier` at the next stage. The mysql
 *  `#`-comment form is the only engine-conditional skip here. */
export function skipWsCommentsLocal(
  source: string,
  i: number,
  end: number,
  options: ParseOptions,
): number {
  const allowHash = options.engine === 'mysql';
  let p = i;
  while (p < end) {
    const c = source[p];
    if (c === ' ' || c === '\t' || c === '\n' || c === '\r') { p++; continue; }
    if (c === '-' && source[p + 1] === '-') {
      p += 2;
      while (p < end && source[p] !== '\n') p++;
      continue;
    }
    if (c === '/' && source[p + 1] === '*') {
      p += 2;
      while (p < end && !(source[p] === '*' && source[p + 1] === '/')) p++;
      if (p < end) p += 2;
      continue;
    }
    if (allowHash && c === '#') {
      p += 1;
      while (p < end && source[p] !== '\n') p++;
      continue;
    }
    break;
  }
  return p;
}

/** Try to consume a specific unquoted keyword at `i` (case-insensitive,
 *  word-bounded). Returns the new index if matched, otherwise `i` unchanged.
 *  The caller is responsible for trimming whitespace before/after. */
export function tryConsumeKeyword(
  source: string,
  i: number,
  end: number,
  options: ParseOptions,
  keyword: string,
): number {
  const ident = readIdentifier(source, i, end, options);
  if (!ident || ident.quoted) return i;
  if (ident.value.toUpperCase() !== keyword.toUpperCase()) return i;
  return ident.end;
}

/** Find the matching `)` for the `(` at `openParen`. Walks the body, skipping
 *  strings / comments / quoted identifiers, tracking paren depth at depth 1
 *  (the `(` itself is depth 1). Returns the offset of the matching `)`, or
 *  `-1` if no matching paren exists in `[openParen+1, end)`. */
export function findMatchingParen(
  source: string,
  options: ParseOptions,
  openParen: number,
  end: number,
): number {
  if (source[openParen] !== '(') return -1;
  const ctx = skipContextFor(options);
  let depth = 1;
  let i = openParen + 1;
  while (i < end) {
    const ident = readIdentifier(source, i, end, options);
    if (ident && (ident.quoted || isBareIdentStart(source, i, options.engine))) { i = ident.end; continue; }
    const skipped = trySkipNonCode(source, i, end, ctx);
    if (skipped >= 0 && skipped > i) { i = skipped; continue; }
    const c = source[i];
    if (c === '(') { depth++; i++; continue; }
    if (c === ')') {
      depth--;
      if (depth === 0) return i;
      i++;
      continue;
    }
    i++;
  }
  return -1;
}

/** Scan `[start, end)` for an unquoted keyword whose upper-case form matches
 *  any of `targets`, ignoring strings / comments / parenthesised content.
 *  Returns the first match's `[start, end]` index pair, or null. */
export function findKeywordAtTopLevel(
  source: string,
  options: ParseOptions,
  start: number,
  end: number,
  targets: ReadonlySet<string>,
): { readonly start: number; readonly end: number; readonly upper: string } | null {
  const ctx = skipContextFor(options);
  let depth = 0;
  let i = start;
  while (i < end) {
    const ident = readIdentifier(source, i, end, options);
    if (ident && isBareIdentStart(source, i, options.engine) && !ident.quoted) {
      if (depth === 0) {
        const upper = ident.value.toUpperCase();
        if (targets.has(upper)) {
          return { start: ident.start, end: ident.end, upper };
        }
      }
      i = ident.end;
      continue;
    }
    if (ident) { i = ident.end; continue; }
    const skipped = trySkipNonCode(source, i, end, ctx);
    if (skipped >= 0 && skipped > i) { i = skipped; continue; }
    const c = source[i];
    if (c === '(') { depth++; i++; continue; }
    if (c === ')') { if (depth > 0) depth--; i++; continue; }
    i++;
  }
  return null;
}
